import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import GSApi from 'api/Api';
import { useNavigation } from 'hooks/useNavigation';
import { useApp } from 'providers/useApp/AppContext';
import { useDataFetcher } from 'hooks/useDataFetcher/useDataFetcher';
import { useGlobalNetworkError, useGlobalNoInternet, withNetworkCheck } from 'hooks/withNetworkCheck/withNetworkCheck';
import { FetchPlayingAsset, FetchParamById, StreamIDKey } from 'types/ApiTypes';
import { useAuth } from 'providers/useAuth/AuthContext';
import { usePlayer } from 'providers/player/PlayerContext';
import { useRecording } from 'providers/useRecording/RecordingContext';
import { RecordingEvent, RecordingPlayerInitiator } from 'types/RecordingTypes';
import { PlayerEvents, RecordingPlayerAssetInfo, PlayingAssetType } from 'types/Player';
import { PageErrorNotFound } from 'components/Pages/PageErrorNotFound/PageErrorNotFound';
import { PlayerWrapper } from 'components/Player/PlayerWrapper';
import { getBookmark } from 'utils/playbackPosition';
import { getErrorStatus } from 'utils/fnError';
import { HelmetWrapper } from 'components/HelmetWrapper/HelmetWrapper';
import { getPlayerPageHelmetData } from 'utils/fnHelmet';
import { withPlayer } from 'hooks/withPlayer/withPlayer';
import { StaticPageRoutes } from 'types/RouteTypes';
import { getListingIdFromRecordingId } from 'utils/recordingUtils';
import CastHandler from 'providers/cast/CastHandler';
import PlaybackManager from 'providers/player/PlaybackManager';
import PageTakeover from 'components/Pages/Settings/PageTakeover/PageTakeover';

const INITIATOR = 'initiator';

const RecordingPlayerPage: FC<{ recordingId: string }> = ({ recordingId }) => {
    const { onBackPressed, navigate, location } = useNavigation();
    const { appHistory, historyLength, popDetailLocations } = useApp();

    const { onNoInternet } = useGlobalNoInternet();
    const { onNetworkError } = useGlobalNetworkError();

    const sessionCreated = useRef<boolean>(false);

    const { recordingsResponse } = useRecording();
    const listingId = getListingIdFromRecordingId(recordingId, recordingsResponse?.results || []);
    const bookmark = getBookmark(listingId, 'recording');

    const { response, error, loading, fetcher, reset } = useDataFetcher<any, FetchPlayingAsset>(
        () => GSApi.createStreamSession('RECORDING', StreamIDKey.RECORDING_ID, recordingId, {}, (res) => res),
        onNoInternet,
        onNetworkError
    );

    const { response: listingDetails, fetcher: listingDetailsFetcher } = useDataFetcher<any, FetchParamById>(
        () => GSApi.fetchListing(listingId),
        onNoInternet,
        onNetworkError
    );

    const { isGuest, openGuestAlert, isSignedOutNotice } = useAuth();
    const {
        asset: currentPlayerAsset,
        setAsset,
        addEventListener,
        removeEventListener,
        resetPlayerAsset,
        isMini,
        closeMiniPlayer,
        setStreamEndReached,
    } = usePlayer();

    const {
        addEventListener: addRecordingEventListener,
        removeEventListener: removeRecordingEventListener,
        updateIsRecorded,
    } = useRecording();

    const [initiator, setInitiator] = useState<RecordingPlayerInitiator>(null);

    const fetchDetails = () => {
        if (!isGuest && listingId) {
            if (!CastHandler.isCast()) {
                fetcher(
                    {
                        id: listingId,
                        createSession: !sessionCreated.current,
                    },
                    true
                );
            }
            listingDetailsFetcher({ id: listingId });
        }
    };

    const onStreamEndReached = () => {
        if (isMini) {
            closeMiniPlayer();
        } else {
            onBackPressed();
        }
    };

    const onRecordingDeleted = useCallback(() => {
        setStreamEndReached(true);

        if (historyLength === 1) {
            navigate(!initiator || initiator === 'recording' ? StaticPageRoutes.RECORDINGS : '/', { replace: true });
        } else if (!initiator || initiator === 'recording') {
            let depth = 0;

            for (let i = historyLength - 1; i > 0; i -= 1) {
                if (appHistory[i].path !== StaticPageRoutes.RECORDINGS) {
                    depth += 1;
                } else {
                    break;
                }
            }

            navigate(-depth);
            popDetailLocations();
        } else {
            navigate(-1);
        }
    }, [initiator]);

    useEffect(() => {
        const urlQueryParams = new URLSearchParams(location.search);
        setInitiator(urlQueryParams.get(INITIATOR) as RecordingPlayerInitiator);
    }, [location.search]);

    useEffect(() => {
        resetPlayerAsset();

        if (CastHandler.isCast()) {
            const castAsset = PlaybackManager.getPlayingAsset();
            if (castAsset) {
                sessionCreated.current = true;
                setAsset(castAsset);
                return;
            }
        }

        sessionCreated.current = false;
    }, [recordingId]);

    useEffect(() => {
        if (listingId) {
            fetchDetails();
        }
    }, [listingId]);

    useEffect(() => {
        if (response && listingDetails) {
            const { duration, start, program } = listingDetails;

            const updatedAsset = {
                ...response,
                bookmark,
                duration,
                startTime: start * 1000,
                endTime: (start + duration) * 1000,
                type: PlayingAssetType.RECORDING,
                title: program?.title,
                image: listingDetails?.program?.background_image_url,
                description: program?.description,
                cast: program?.credits,
                listingId,
                recording_id: recordingId,
                episode_title: program?.episode_title || program?.episode_title_original,
                seriesId: program?.program_series_id,
            };

            let assetToSet: RecordingPlayerAssetInfo;

            if (!sessionCreated.current) {
                sessionCreated.current = true;
                PlaybackManager.startHeartbeat(response.id, response.heartbeat_interval);
                assetToSet = updatedAsset;
            } else {
                assetToSet = {
                    ...updatedAsset,
                    manifestUrl: currentPlayerAsset?.manifestUrl,
                };
            }

            setAsset(assetToSet);
        }
    }, [response, listingDetails]);

    useEffect(() => {
        addEventListener(PlayerEvents.END_OF_STREAM, onStreamEndReached);

        return () => {
            removeEventListener(PlayerEvents.END_OF_STREAM, onStreamEndReached);
        };
    }, [response, isMini]);

    useEffect(() => {
        if (isGuest) {
            openGuestAlert('/');
        }

        updateIsRecorded(true);

        addRecordingEventListener(RecordingEvent.DELETE, onRecordingDeleted);
        return () => removeRecordingEventListener(RecordingEvent.DELETE, onRecordingDeleted);
    }, [recordingId, initiator]);

    if (error && !isSignedOutNotice) {
        if (getErrorStatus(error) === 409) {
            return (
                <PageTakeover
                    limitReached
                    onSessionDeleted={() => {
                        reset();
                        fetcher(null);
                    }}
                />
            );
        }

        return <PageErrorNotFound onButtonClick={closeMiniPlayer} />;
    }

    return (
        <>
            {response && <HelmetWrapper {...getPlayerPageHelmetData(listingDetails)} />}
            <PlayerWrapper loading={loading} />
        </>
    );
};

export default withNetworkCheck(withPlayer(RecordingPlayerPage));
